import {
    lazy,
    Suspense,
    useState,
    useEffect,
    type FC,
    type MouseEventHandler,
    type ReactNode,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFragment, useRelayEnvironment, graphql } from 'react-relay';
import { isMasqueradingAsBuyer, isDirectlyLoggedInAsUser } from 'dibs-cookie-jar';
import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';
import { trackEvent, eventNameConstants, interactionTypeConstants } from 'dibs-tracking';

import Dropdown, { alignConstants, sizeConstants } from '../Dropdown/Dropdown.tsx';
import DropdownBody from '../Dropdown/DropdownBody.tsx';
import authModalLoader from '../../utils/AuthModalLoader.js';
import AccountNavTrigger from './AccountNavTrigger.tsx';
import BuyerAccountNavContent from './AccountNavContent.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { ACTION_REGISTER, ACTION_LOGIN } from './accountConstants.ts';

import dibsCss from 'dibs-css';
import styles from './AccountNav-style.scss';
import { actionCreators } from '../../actions/index.ts';

import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent.tsx';

import { type AccountNav_user$key } from './__generated__/AccountNav_user.graphql.ts';
import { type AccountNav_viewer$key } from './__generated__/AccountNav_viewer.graphql.ts';

const TradeAccountNavContentLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "tradeAccountNavContent" */
            './TradeAccountNavContent.tsx'
        )
);

const AccountNavContainer: FC<{ children: ReactNode }> = ({ children }) => (
    <div
        data-tn="account-nav"
        className={classnames(dibsCss.flex, dibsCss.relative, dibsCss.hFull)}
    >
        {children}
    </div>
);

type ReduxStoreType = {
    header: {
        isVipCuratedFolderTooltipVisible: boolean;
    };
};

type AccountNavProps = {
    hasUser: boolean;
    hasUserId: boolean;
    isClient: boolean;
    user: AccountNav_user$key;
    hasTradeUserId: boolean;
    viewer: AccountNav_viewer$key;
    hasRootPendingRefetch: boolean;
};

const AccountNav: FC<AccountNavProps> = ({
    hasUser,
    hasUserId,
    isClient,
    user: userRef,
    hasTradeUserId,
    viewer: viewerRef,
    hasRootPendingRefetch,
}) => {
    const [isMasquerading, setIsMasquerading] = useState(false);
    const [isDirectLogin, setIsDirectLogin] = useState(false);
    const relayEnvironment = useRelayEnvironment();
    const user = useFragment(
        graphql`
            fragment AccountNav_user on User {
                ...AccountNavContent_user @defer
                ...AccountNavTrigger_user @defer
                ...TradeAccountNavContent_user @include(if: $hasTradeUserId) @defer
                isVerifiedTrade
            }
        `,
        userRef
    );
    const viewer = useFragment(
        graphql`
            fragment AccountNav_viewer on Viewer {
                ...AccountNavContent_viewer
                ...TradeAccountNavContent_viewer
            }
        `,
        viewerRef
    );
    useEffect(() => {
        setIsMasquerading(isMasqueradingAsBuyer(document.cookie));
        setIsDirectLogin(isDirectlyLoggedInAsUser(document.cookie));
    }, []);

    const isVipCuratedFolderTooltipVisible = useSelector<ReduxStoreType, boolean>(
        ({ header }) => header.isVipCuratedFolderTooltipVisible
    );
    const dispatch = useDispatch();

    const handleClick: MouseEventHandler<HTMLButtonElement> = async e => {
        e.preventDefault();
        const action = e.currentTarget.dataset.action;
        try {
            const { authError } = await authModalLoader.show({
                relayEnvironment,
                email: localStorage.getItem('userEmail'),
                action,
                ga: {
                    label: `${action} link - global header`,
                    value: !!window.scrollY,
                },
            });
            if (!authError) {
                dispatch(actionCreators.setUpdateUserState(true));
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Auth Modal could not be loaded\n', error);
        }
    };

    if (!isClient || (hasUserId && !hasUser)) {
        return null;
    }

    const isVerifiedTrade = user?.isVerifiedTrade;

    if (!hasUser) {
        return (
            <AccountNavContainer>
                <div className={classnames(dibsCss.itemsCenter, dibsCss.flex)}>
                    <Link
                        className={classnames(
                            styles.authButton,
                            dibsCss.sassyFontBodySizeSmallTypeNone,
                            dibsCss.cursorPointer
                        )}
                        dataTn="user-menu-nav-item-link-log-in"
                        dataAction={ACTION_LOGIN}
                        onClick={handleClick}
                    >
                        <FormattedMessage
                            id="dbl.Header.AccountNav.log_in"
                            defaultMessage={'Log In'}
                        />
                    </Link>
                    <Link
                        className={classnames(
                            styles.authButton,
                            styles.borderLeft,
                            dibsCss.cursorPointer,
                            dibsCss.sassyFontBodySizeSmallTypeNone
                        )}
                        dataTn="user-menu-nav-item-link-sign-up"
                        dataAction={ACTION_REGISTER}
                        onClick={handleClick}
                    >
                        <FormattedMessage
                            id="dbl.Header.AccountNav.sign_up"
                            defaultMessage={'Sign Up'}
                        />
                    </Link>
                </div>
            </AccountNavContainer>
        );
    }

    return (
        <AccountNavContainer>
            {isVipCuratedFolderTooltipVisible ? (
                <Suspense fallback={null}>
                    <AccountNavTrigger
                        isMasquerading={isMasquerading}
                        isDirectLogin={isDirectLogin}
                        user={user}
                        hasRootPendingRefetch={hasRootPendingRefetch}
                    />
                </Suspense>
            ) : (
                <Dropdown
                    align={alignConstants.ALIGN_CENTER}
                    size={sizeConstants.SIZE_SMALL}
                    bodyClassName={styles.dropdownBodyWrapper}
                    dropdownClassName={styles.dropdown}
                    onShow={() => {
                        trackEvent({
                            eventName: eventNameConstants.EVENT_PROMO_INTERACTION,
                            interaction_type: interactionTypeConstants.INTERACTION_TYPE_GLOBAL_NAV,
                            step_interaction_name: 'user nav dropdown displayed',
                        });
                    }}
                >
                    <Suspense fallback={null}>
                        <AccountNavTrigger
                            isMasquerading={isMasquerading}
                            isDirectLogin={isDirectLogin}
                            user={user}
                            hasRootPendingRefetch={hasRootPendingRefetch}
                        />
                    </Suspense>
                    <DropdownBody>
                        {isVerifiedTrade && hasTradeUserId ? (
                            <Suspense fallback={<DropdownLoadingContent />}>
                                <TradeAccountNavContentLazy user={user} viewer={viewer} />
                            </Suspense>
                        ) : (
                            <Suspense fallback={<DropdownLoadingContent />}>
                                <BuyerAccountNavContent user={user} viewer={viewer} />
                            </Suspense>
                        )}
                    </DropdownBody>
                </Dropdown>
            )}
        </AccountNavContainer>
    );
};

export default AccountNav;
